import { useState, useEffect, useCallback } from 'react';
import { Button, Form } from 'react-bootstrap';
import { AppModal } from '../components/AppModal';
import {
  getStaffCreatorList,
  getStaffCreatorSummary,
  getStaffCreatorTransactions,
  createCreator,
  updateCreator,
  payoutTransactions,
  releaseTransaction,
  rejectTransaction,
} from '../services/CreatorService';
import { getUserData } from '../services/UserService';
import { useAccount } from '../Context/AccountProvider';
import { AccountSearch } from '../components/AccountSearch';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { EmptyState } from '../components/EmptyState';
import { FormFeedback } from '../components/FormFeedback';
import { StatusBadge } from '../components/StatusBadge';
import { StaffPageHeader } from '../components/StaffPageHeader';
import { PersonBadgeFill } from 'react-bootstrap-icons';

// ── Tab constants ─────────────────────────────────────────────────────────────
const TAB_SUMMARY  = 'summary';
const TAB_CREATORS = 'creators';
const TAB_DETAIL   = 'detail';

function StaffCreatorManagement() {
  const { updateAccountInfo } = useAccount();
  const [tab, setTab] = useState(TAB_SUMMARY);
  const [summary, setSummary] = useState(null);
  const [creators, setCreators] = useState([]);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create / edit modal state
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // null = create mode
  const [modalForm, setModalForm] = useState({ code: '', revenuePct: '10', minCoins: '5', enabled: true });
  const [modalAccount, setModalAccount] = useState(null); // selected account for create mode
  const [modalError, setModalError] = useState(null);
  const [modalSaving, setModalSaving] = useState(false);

  // Payout modal state
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedTxIds, setSelectedTxIds] = useState(new Set());
  const [payoutRef, setPayoutRef] = useState('');
  const [payoutError, setPayoutError] = useState(null);
  const [payoutSaving, setPayoutSaving] = useState(false);

  const loadCreators = useCallback(async () => {
    try {
      const [creatorsData, summaryData] = await Promise.all([
        getStaffCreatorList(),
        getStaffCreatorSummary(),
      ]);
      setCreators(creatorsData);
      setSummary(summaryData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [creatorsData, summaryData] = await Promise.all([
          getStaffCreatorList(),
          getStaffCreatorSummary(),
        ]);
        if (!cancelled) {
          setCreators(creatorsData);
          setSummary(summaryData);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function openDetail(creator) {
    setSelectedCreator(creator);
    setTransactions([]);
    setTab(TAB_DETAIL);
    try {
      const txs = await getStaffCreatorTransactions(creator.id);
      setTransactions(txs);
    } catch (err) {
      setError(err.message);
    }
  }

  // ── Create / edit modal ───────────────────────────────────────────────────

  function openCreateModal() {
    setEditTarget(null);
    setModalForm({ code: '', revenuePct: '10', minCoins: '5', enabled: true });
    setModalAccount(null);
    setModalError(null);
    setShowModal(true);
  }

  function openEditModal(creator) {
    setEditTarget(creator);
    setModalForm({
      code: creator.code,
      revenuePct: String(creator.revenuePct),
      minCoins: String(creator.minCoins),
      enabled: creator.enabled,
    });
    setModalAccount(null);
    setModalError(null);
    setShowModal(true);
  }

  async function handleModalSave() {
    if (!editTarget && !modalAccount) {
      setModalError('Please select an account.');
      return;
    }
    setModalSaving(true);
    setModalError(null);
    try {
      if (editTarget) {
        await updateCreator(
          editTarget.id,
          modalForm.code,
          parseFloat(modalForm.revenuePct),
          parseInt(modalForm.minCoins, 10),
          modalForm.enabled
        );
      } else {
        await createCreator(
          modalAccount.id,
          modalForm.code,
          parseFloat(modalForm.revenuePct),
          parseInt(modalForm.minCoins, 10)
        );
      }
      setShowModal(false);
      await loadCreators();
      // Refresh session user in case the saved creator is the current user,
      // so nav links (Creator Dashboard) update without requiring a re-login.
      try {
        const userData = await getUserData();
        updateAccountInfo({ user: userData });
      } catch { /* non-critical */ }
    } catch (err) {
      const messages = {
        'code-taken': 'That creator code is already in use.',
        'invalid-code-format': 'Code must be alphanumeric only.',
        'invalid-pct': 'Revenue % must be between 1 and 100.',
        'req': 'All fields are required.',
      };
      setModalError(messages[err.message] ?? 'Failed to save creator.');
    } finally {
      setModalSaving(false);
    }
  }

  // ── Payout modal ──────────────────────────────────────────────────────────

  function openPayoutModal() {
    setSelectedTxIds(new Set());
    setPayoutRef('');
    setPayoutError(null);
    setShowPayoutModal(true);
  }

  function toggleTx(id) {
    setSelectedTxIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function handlePayout() {
    if (selectedTxIds.size === 0 || !payoutRef.trim()) {
      setPayoutError('Select at least one transaction and enter a payout reference.');
      return;
    }
    setPayoutSaving(true);
    setPayoutError(null);
    try {
      await payoutTransactions(Array.from(selectedTxIds), payoutRef.trim());
      setShowPayoutModal(false);
      const txs = await getStaffCreatorTransactions(selectedCreator.id);
      setTransactions(txs);
      await loadCreators();
    } catch {
      setPayoutError('Payout failed. Please try again.');
    } finally {
      setPayoutSaving(false);
    }
  }

  // ── Per-row actions ───────────────────────────────────────────────────────

  async function handleRelease(txId) {
    try {
      await releaseTransaction(txId);
      const txs = await getStaffCreatorTransactions(selectedCreator.id);
      setTransactions(txs);
    } catch { /* ignore */ }
  }

  async function handleReject(txId) {
    try {
      await rejectTransaction(txId);
      const txs = await getStaffCreatorTransactions(selectedCreator.id);
      setTransactions(txs);
    } catch { /* ignore */ }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="scm-error">Failed to load creator data.</p>;

  const pendingTxs = transactions.filter(t => t.status === 'pending');

  return (
    <div className="scm-page">
      <StaffPageHeader
        icon={<PersonBadgeFill />}
        title="Creator Codes"
        actions={<Button className="scm-btn-create" onClick={openCreateModal}>Add Creator</Button>}
      />

      <div className="scm-tabs">
        <button
          className={`scm-tab${tab === TAB_SUMMARY ? ' scm-tab--active' : ''}`}
          onClick={() => setTab(TAB_SUMMARY)}
        >Overview</button>
        <button
          className={`scm-tab${tab === TAB_CREATORS ? ' scm-tab--active' : ''}`}
          onClick={() => setTab(TAB_CREATORS)}
        >Creators</button>
        {selectedCreator && (
          <button
            className={`scm-tab${tab === TAB_DETAIL ? ' scm-tab--active' : ''}`}
            onClick={() => setTab(TAB_DETAIL)}
          >{selectedCreator.code.toUpperCase()}</button>
        )}
      </div>

      {/* ── Summary tab ─────────────────────────────────────────────────── */}
      {tab === TAB_SUMMARY && summary && (
        <div className="scm-summary">
          <div className="scm-totals-row">
            <div className="scm-total-card">
              <span className="scm-total-label">Total Pending</span>
              <span className="scm-total-value scm-total-value--pending">${summary.totalPending.toFixed(2)}</span>
            </div>
            <div className="scm-total-card">
              <span className="scm-total-label">Total Paid Out (Lifetime)</span>
              <span className="scm-total-value scm-total-value--paid">${summary.totalPaidOut.toFixed(2)}</span>
            </div>
          </div>

          {summary.creatorsWithPending.length === 0
            ? <EmptyState>No creators have pending payouts.</EmptyState>
            : (
              <div className="scm-pending-list">
                <h3 className="scm-section-title">Creators Awaiting Payout</h3>
                <table className="scm-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Account</th>
                      <th>Pending Txs</th>
                      <th>Pending Total</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.creatorsWithPending.map(c => (
                      <tr key={c.creatorId}>
                        <td className="scm-code">{c.code.toUpperCase()}</td>
                        <td>{c.accountName}</td>
                        <td>{c.pendingCount}</td>
                        <td>${c.pendingTotal.toFixed(2)}</td>
                        <td>
                          <button
                            className="scm-link-btn"
                            onClick={() => openDetail(creators.find(cr => cr.id === c.creatorId) ?? { id: c.creatorId, code: c.code, accountId: c.accountId })}
                          >View</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </div>
      )}

      {/* ── Creators list tab ───────────────────────────────────────────── */}
      {tab === TAB_CREATORS && (
        <div className="scm-creators">
          {creators.length === 0
            ? <EmptyState ctaLabel="Add Creator" ctaTo={null}>No creators configured yet.</EmptyState>
            : (
              <table className="scm-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Account</th>
                    <th>Rev %</th>
                    <th>Min Coins</th>
                    <th>Status</th>
                    <th>Pending</th>
                    <th>Paid Out</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {creators.map(c => (
                    <tr key={c.id}>
                      <td className="scm-code">{c.code.toUpperCase()}</td>
                      <td>{c.accountName}</td>
                      <td>{c.revenuePct}%</td>
                      <td>{c.minCoins}</td>
                      <td>
                        {c.enabled
                          ? <span className="scm-badge scm-badge--active">Active</span>
                          : <span className="scm-badge scm-badge--disabled">Disabled</span>}
                      </td>
                      <td>${c.pendingTotal.toFixed(2)}</td>
                      <td>${c.paidTotal.toFixed(2)}</td>
                      <td className="scm-actions">
                        <button className="scm-link-btn" onClick={() => openDetail(c)}>Transactions</button>
                        <button className="scm-link-btn" onClick={() => openEditModal(c)}>Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </div>
      )}

      {/* ── Creator detail tab ─────────────────────────────────────────── */}
      {tab === TAB_DETAIL && selectedCreator && (
        <div className="scm-detail">
          <div className="scm-detail-header">
            <div>
              <span className="scm-code">{selectedCreator.code?.toUpperCase()}</span>
              <span className="scm-detail-account">{selectedCreator.accountName}</span>
            </div>
            {pendingTxs.length > 0 && (
              <Button className="scm-btn-payout" onClick={openPayoutModal}>
                Pay Out Selected
              </Button>
            )}
          </div>

          {transactions.length === 0
            ? <EmptyState>No transactions for this creator.</EmptyState>
            : (
              <div className="scm-tx-table-wrap">
                <table className="scm-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Player</th>
                      <th>Coins</th>
                      <th>Net Rev</th>
                      <th>Payout</th>
                      <th>Status</th>
                      <th>Payout Ref</th>
                      <th>Date</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(tx => (
                      <tr key={tx.id}>
                        <td>
                          {tx.status === 'pending' && (
                            <input
                              type="checkbox"
                              checked={selectedTxIds.has(tx.id)}
                              onChange={() => toggleTx(tx.id)}
                            />
                          )}
                        </td>
                        <td>{tx.payerCharacterName}</td>
                        <td>{tx.coinsBought}</td>
                        <td>${tx.netRevenue.toFixed(4)}</td>
                        <td>${tx.payoutAmount.toFixed(4)}</td>
                        <td><StatusBadge status={tx.status} /></td>
                        <td>{tx.payoutRefId ?? '—'}</td>
                        <td>{new Date(tx.transactionDate).toLocaleDateString()}</td>
                        <td className="scm-actions">
                          {tx.status === 'creator_disabled' && (
                            <button className="scm-link-btn" onClick={() => handleRelease(tx.id)}>Release</button>
                          )}
                          {tx.status !== 'paid_out' && tx.status !== 'payout_rejected' && (
                            <button className="scm-link-btn scm-link-btn--danger" onClick={() => handleReject(tx.id)}>Reject</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </div>
      )}

      {/* ── Create / Edit modal ────────────────────────────────────────── */}
      <AppModal show={showModal} onHide={() => setShowModal(false)}>
        <AppModal.Header closeButton>
          <AppModal.Title>{editTarget ? 'Edit Creator' : 'Add Creator'}</AppModal.Title>
        </AppModal.Header>
        <AppModal.Body>
          <Form className="modal-form">
            {!editTarget && (
              <div className="scm-field mb-3">
                <label className="form-label">Account</label>
                <AccountSearch value={modalAccount} onChange={setModalAccount} />
              </div>
            )}
            <Form.Group className="scm-field mb-3">
              <Form.Label>Creator Code</Form.Label>
              <Form.Control
                type="text"
                value={modalForm.code}
                onChange={e => setModalForm(f => ({ ...f, code: e.target.value }))}
                placeholder="Alphanumeric, case-insensitive"
                maxLength={32}
              />
            </Form.Group>
            <Form.Group className="scm-field mb-3">
              <Form.Label>Revenue Share %</Form.Label>
              <Form.Control
                type="number"
                min="0.01"
                max="100"
                step="0.01"
                value={modalForm.revenuePct}
                onChange={e => setModalForm(f => ({ ...f, revenuePct: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="scm-field mb-3">
              <Form.Label>Minimum Coins per Transaction</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={modalForm.minCoins}
                onChange={e => setModalForm(f => ({ ...f, minCoins: e.target.value }))}
              />
            </Form.Group>
            {editTarget && (
              <Form.Group className="scm-field scm-field--check mb-3">
                <Form.Check
                  type="checkbox"
                  label="Creator enabled"
                  checked={modalForm.enabled}
                  onChange={e => setModalForm(f => ({ ...f, enabled: e.target.checked }))}
                />
              </Form.Group>
            )}
            <FormFeedback type="error" message={modalError} />
            <div className="modal-form-actions">
              <Button variant="secondary" onClick={() => setShowModal(false)} disabled={modalSaving}>Cancel</Button>
              <Button variant="success" onClick={handleModalSave} disabled={modalSaving}>
                {modalSaving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </Form>
        </AppModal.Body>
      </AppModal>

      {/* ── Payout modal ───────────────────────────────────────────────── */}
      <AppModal show={showPayoutModal} onHide={() => setShowPayoutModal(false)}>
        <AppModal.Header closeButton>
          <AppModal.Title>Record Payout</AppModal.Title>
        </AppModal.Header>
        <AppModal.Body>
          <Form className="modal-form">
            <p className="scm-payout-desc">
              Select the transactions to mark as paid out, then enter the reference ID for this payout.
            </p>
            <div className="scm-payout-tx-list">
              {pendingTxs.map(tx => (
                <label key={tx.id} className="scm-payout-row">
                  <input
                    type="checkbox"
                    checked={selectedTxIds.has(tx.id)}
                    onChange={() => toggleTx(tx.id)}
                  />
                  <span className="scm-payout-player">{tx.payerCharacterName}</span>
                  <span className="scm-payout-amount">${tx.payoutAmount.toFixed(4)}</span>
                  <span className="scm-payout-date">{new Date(tx.transactionDate).toLocaleDateString()}</span>
                </label>
              ))}
            </div>
            <div className="scm-payout-total">
              Selected: ${pendingTxs.filter(t => selectedTxIds.has(t.id)).reduce((sum, t) => sum + t.payoutAmount, 0).toFixed(4)}
            </div>
            <Form.Group className="scm-field mb-3">
              <Form.Label>Payout Reference ID</Form.Label>
              <Form.Control
                type="text"
                value={payoutRef}
                onChange={e => setPayoutRef(e.target.value)}
                placeholder="e.g. PayPal transaction ID"
              />
            </Form.Group>
            <FormFeedback type="error" message={payoutError} />
            <div className="modal-form-actions">
              <Button variant="secondary" onClick={() => setShowPayoutModal(false)} disabled={payoutSaving}>Cancel</Button>
              <Button variant="success" onClick={handlePayout} disabled={payoutSaving}>
                {payoutSaving ? 'Processing…' : 'Confirm Payout'}
              </Button>
            </div>
          </Form>
        </AppModal.Body>
      </AppModal>
    </div>
  );
}

export { StaffCreatorManagement };
